import { types as api } from '@mesa-labs/mesa-api';
import { InvoiceApi } from '@mesa-labs/mesa-api/dist/apis/invoice';
import { CsvFormat, InvoiceFilterParams } from '@mesa-labs/mesa-api/dist/types';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { appendFilterParams } from '../../utils';
import { VendorAdjustmentBalancesExportParams } from '../../utils/adjustments';
import { getIdToken, prepareHeaders, StatisticsMutex, useApi, } from './api';

/**
 * Any matching mutation can change any matching view: a persisted run writes matches, and a
 * confirm/reject moves rows between states and shifts a run's counts. Rather than reason about
 * which view survives which action, every matching mutation invalidates all of them.
 *
 * This covers the invoicesApi slice only — the dashboard's invoice, transaction and metrics
 * queries live in other slices, and RTK Query tags never cross a createApi boundary. The
 * matchingCacheInvalidation listener middleware handles those.
 */
const MatchingMutationInvalidations = [
  'TransactionMatches',
  'TransactionMatchingRuns',
  'CollectionMatchSignals',
  'ScheduledVendorCollections',
  'ScheduledInvoiceCollections',
] as const;

export const invoicesApi = createApi({
  reducerPath: 'invoicesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CONFIG.api.invoiceUrl,
    prepareHeaders: async (original) => {
      const headers = await prepareHeaders(original);
      headers.delete('Content-Type'); // cannot be supplied with uploadRemittanceAdvice
      return headers;
    },
  }),
  tagTypes: [
    'Invoice', 'InvoiceAudit', 'Invoices', 'InvoiceLineItem', 'InvoiceLineItems', 'InvoiceAdjustments', 'AdjustmentGroups', 'Collections',
    'CollectionInvoices', 'ReconciliationPayloads', 'ReconciliationPayload', 'Clients', 'Client', 'ClientMappings', 'ClientMapping', 'ClientMappingOverrides', 'ClientExclusions', 'TaskSchedules', 'TaskExecutions', 'BatchSweeps', 'BatchSweep', 'RepaymentItemsForBatchSweep',
    'ConfigurationSettings', 'InvoicesNeedingApproval', 'ClientVendorAnomalies', 'InvoiceQuarantines', 'VendorClientRestrictions', 'InvoiceDocuments', 'ScheduledVendorCollections', 'ScheduledInvoiceCollections',
    'CollectionMatchSignals', 'AprDashboard', 'TransactionMatches', 'TransactionMatchingParams', 'ReportMetadata', 'TransactionMatchingRuns'
  ],
  endpoints: (build) => ({
    getAllInvoices: build.query<api.IPagedResults<api.InvoiceResponse>, api.InvoiceFilterParams>({
      async queryFn(params) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getAllInvoices<api.InvoiceResponse>(params);
        return { data };
      },
      providesTags: ['Invoices'],
    }),
    getVendorInvoiceClients: build.query<api.VendorInvoiceClientsResponse, { vendorId: api.UUID }>({
      async queryFn({ vendorId }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getVendorInvoiceClients<api.VendorInvoiceClientsResponse>(vendorId);
        return { data };
      },
      providesTags: ['Invoices'],
    }),
    getInvoice: build.query<api.InvoiceAggregateResponse | undefined, { id: api.UUID }>({
      async queryFn({ id }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getInvoice<api.InvoiceAggregateResponse>(id);
        return { data };
      },
      providesTags: (result, error, id) => (result
        ? [{ type: 'Invoice', ...id, ...result }]
        : ['Invoice']),
    }),
    getAllReconciliationPayloads: build.query<api.IPagedResults<api.ReconciliationPayloadResponse>, api.ReconciliationPayloadFilterParams>({
      async queryFn(params) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getAllReconciliationPayloads<api.ReconciliationPayloadResponse>(params);
        return { data };
      },
      providesTags: ['ReconciliationPayloads'],
    }),
    getAllBatchSweeps: build.query<api.IPagedResults<api.BatchSweepResponse>, api.GetBatchSweepsFilterParams>({
      async queryFn(params) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getBatchSweeps(params);
        return { data };
      },
      providesTags: ['BatchSweeps'],
    }),
    getBatchSweep: build.query<api.BatchSweepResponse, { id: api.UUID }>({
      async queryFn({ id }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getBatchSweep(id);
        return { data };
      },
      providesTags: (result, error, id) => (result
        ? [{ type: 'BatchSweep', ...id, ...result }]
        : ['BatchSweep']),
    }),
    getRepaymentItemsForBatchSweep: build.query<api.GetPrincipalRepaymentItemsForBatchSweepResponse, { id: api.UUID } & api.GetPrincipalRepaymentItemsForBatchSweepFilterParams>({
      async queryFn({ id, ...params }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getRepaymentItemsForBatchSweep(id, params);
        return { data };
      },
      providesTags: (result, error, id) => (result
        ? [{ type: 'RepaymentItemsForBatchSweep', ...id, ...result }]
        : ['RepaymentItemsForBatchSweep']),
    }),
    getReconciliationPayloadAggregate: build.query<api.ReconciliationPayloadAggregateResponse, { partnerId: number, payloadIdentifier: string }>({
      async queryFn({ partnerId, payloadIdentifier }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getReconciliationPayloadAggregate<api.ReconciliationPayloadAggregateResponse>(partnerId, payloadIdentifier);
        return { data };
      },
      providesTags: ['ReconciliationPayload'],
    }),
    combineReconcilationPayloads: build.mutation<api.ReconciliationPayloadResponse, api.CombineReconciliationPayloadsRequest>({
      async queryFn(body) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.combineReconciliationPayloads(body);
        return { data };
      },
      invalidatesTags: ['ReconciliationPayloads'],
    }),
    deleteReconciliationPayload: build.mutation<api.ReconciliationPayloadResponse | undefined, { partnerId: number, payloadIdentifier: string }>({
      async queryFn({ partnerId, payloadIdentifier }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.deleteReconciliationPayload(partnerId, payloadIdentifier);
        return { data };
      },
      invalidatesTags: ['ReconciliationPayloads'],
    }),
    uploadReconciliationPayload: build.mutation<readonly api.ReconciliationPayloadAggregateResponse[], { body: FormData, partnerId: number }>({
      async queryFn({ body, partnerId }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.uploadReconciliationPayload(partnerId, body);
        return { data };
      },
      invalidatesTags: ['ReconciliationPayloads'],
    }),
    associateReconciliationPayloadWithCollection: build.mutation<readonly api.InvoiceCollectionAllocationResponse[], { partnerId: number, payloadIdentifier: string, collectionId: string }>({
      async queryFn({
        partnerId,
        payloadIdentifier,
        collectionId,
      }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi
          .associateReconciliationPayloadWithCollection(partnerId, payloadIdentifier, { collectionId });
        return { data };
      },
      invalidatesTags: ['Collections', 'ReconciliationPayloads', 'Invoices', 'Invoice', 'InvoiceAudit'],
    }),
    approveInvoice: build.mutation<api.InvoiceAggregateResponse | undefined, { id: api.UUID, skipInvalidation?: boolean }>({
      async queryFn({ id }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.approveInvoice<api.InvoiceAggregateResponse>(id);
        return { data };
      },
      invalidatesTags: (result, _error, arg) => (arg.skipInvalidation
        ? []
        : result
          ? [{ type: 'Invoice', ...arg, ...result }, 'Invoices', 'InvoiceAudit', 'InvoicesNeedingApproval']
          : ['Invoice', 'Invoices', 'InvoiceAudit', 'InvoicesNeedingApproval']),
    }),
    approveInvoiceLineItem: build.mutation<api.InvoiceLineItemResponse | undefined, { id: api.UUID, skipInvalidation?: boolean }>({
      async queryFn({ id }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.approveInvoiceLineItem<api.InvoiceLineItemResponse>(id);
        return { data };
      },
      invalidatesTags: (_result, _error, arg) => arg.skipInvalidation ? [] : ['InvoiceLineItem', 'InvoiceLineItems', 'InvoiceAudit', 'InvoicesNeedingApproval'],
    }),
    unapproveInvoice: build.mutation<api.InvoiceAggregateResponse | undefined, { id: api.UUID; skipInvalidation?: boolean }>({
      async queryFn({ id }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.unapproveInvoice<api.InvoiceAggregateResponse>(id);
        return { data };
      },
      invalidatesTags: (result, error, args) => (args.skipInvalidation
        ? []
        : result
          ? [{ type: 'Invoice', ...args, ...result }, 'Invoices', 'InvoiceAudit', 'InvoicesNeedingApproval']
          : ['Invoice', 'Invoices', 'InvoiceAudit', 'InvoicesNeedingApproval']),
    }),
    takeSuggestedDueDate: build.mutation<api.InvoiceAggregateResponse | undefined, { id: api.UUID, skipInvalidation?: boolean }>({
      async queryFn({ id }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.takeSuggestedDueDate<api.InvoiceAggregateResponse>(id, {});
        return { data };
      },
      invalidatesTags: (result, _error, args) => (args.skipInvalidation
        ? []
        : result
          ? [{ type: 'Invoice', ...args, ...result }, 'Invoices', 'InvoiceAudit']
          : ['Invoice', 'Invoices', 'InvoiceAudit']),
    }),
    cancelInvoice: build.mutation<api.InvoiceAggregateResponse | undefined, api.CancelInvoiceRequest & { id: string; skipInvalidation?: boolean }>({
      async queryFn({ id, skipInvalidation: _skipInvalidation, ...body }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.cancelInvoice<api.InvoiceAggregateResponse>(id, body);
        return { data };
      },
      invalidatesTags: (result, error, args) => (args.skipInvalidation
        ? []
        : result
          ? [{ type: 'Invoice', ...args, ...result }, 'Invoices', 'InvoiceAudit']
          : ['Invoice', 'Invoices', 'InvoiceAudit']),
    }),
    updateInvoiceCollectionScheme: build.mutation<api.InvoiceAggregateResponse | undefined, api.UpdateInvoiceCollectionSchemeRequest & { id: api.UUID; skipInvalidation?: boolean }>({
      async queryFn({ id, skipInvalidation: _skipInvalidation, ...body }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.updateInvoiceCollectionScheme<api.InvoiceAggregateResponse>(id, body);
        return { data };
      },
      invalidatesTags: (result, error, args) => (args.skipInvalidation
        ? []
        : result
          ? [{ type: 'Invoice', ...args, ...result }, 'Invoices', 'InvoiceAudit', { type: 'ScheduledVendorCollections', vendorId: result.vendorId }, 'ScheduledInvoiceCollections']
          : ['Invoice', 'Invoices', 'InvoiceAudit', 'ScheduledVendorCollections', 'ScheduledInvoiceCollections']),
    }),
    requestInvoiceRedirect: build.mutation<api.InvoiceAggregateResponse | undefined, { id: api.UUID, skipInvalidation?: boolean }>({
      async queryFn({ id }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.requestInvoiceRedirect<api.InvoiceAggregateResponse>(id);
        return { data };
      },
      invalidatesTags: (result, _error, args) => args.skipInvalidation
        ? []
        : (result
          ? [{ type: 'Invoice', ...args, ...result }, 'Invoices', 'InvoiceAudit']
          : ['Invoice', 'Invoices', 'InvoiceAudit']),
    }),
    reopenInvoiceForRedirect: build.mutation<api.InvoiceAggregateResponse | undefined, { id: api.UUID; skipInvalidation?: boolean } & api.ReopenInvoiceForRedirectRequest>({
      async queryFn({ id, skipInvalidation, ...request }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.reopenInvoiceForRedirect<api.InvoiceAggregateResponse>(id, request);
        return { data };
      },
      invalidatesTags: (result, _error, args) => args.skipInvalidation
        ? []
        : (result
          ? [{ type: 'Invoice', ...args, ...result }, 'Invoices', 'InvoiceAudit']
          : ['Invoice', 'Invoices', 'InvoiceAudit']),
    }),
    requestInvoiceFunding: build.mutation<api.InvoiceAggregateResponse | undefined, { id: api.UUID; skipInvalidation?: boolean }>({
      async queryFn({ id }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.requestInvoiceFunding<api.InvoiceAggregateResponse>(id);
        return { data };
      },
      invalidatesTags: (result, _error, args) => args.skipInvalidation
        ? []
        : (result
          ? [{ type: 'Invoice', ...args, ...result }, 'Invoices', 'InvoiceAudit']
          : ['Invoice', 'Invoices', 'InvoiceAudit']),
    }),
    makeInvoiceNotFunded: build.mutation<api.InvoiceAggregateResponse | undefined, { id: string; skipInvalidation?: boolean }>({
      async queryFn({ id }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.makeInvoiceNotFunded<api.InvoiceAggregateResponse>(id);
        return { data };
      },
      invalidatesTags: (result, error, args) => (args.skipInvalidation
        ? []
        : result
          ? [{ type: 'Invoice', ...args, ...result }, 'Invoices', 'InvoiceAudit']
          : ['Invoice', 'Invoices', 'InvoiceAudit']),
    }),
    sendInvoiceUploadRequestedEmail: build.mutation<api.InvoiceAggregateResponse | undefined, { id: api.UUID; skipInvalidation?: boolean }>({
      async queryFn({ id }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.sendInvoiceUploadRequestedEmail<api.InvoiceAggregateResponse>(id);
        return { data };
      },
    }),
    sendVoidedEmail: build.mutation<api.InvoiceAggregateResponse | undefined, { id: api.UUID; refundFee: boolean }>({
      async queryFn({ id, refundFee }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.sendVoidedEmail<api.InvoiceAggregateResponse>(id, { refundFee });
        return { data };
      },
      invalidatesTags: (result, _error, args) =>
        result
          ? [{ type: 'Invoice', ...args, ...result }, 'Invoices', 'InvoiceAudit']
          : ['Invoice', 'Invoices', 'InvoiceAudit'],
    }),
    getOriginationStatistics:
      build.query<api.OriginationRunningStatistic, api.VendorOriginationStatisticsFilterParams>({
        async queryFn(params) {
          return await StatisticsMutex.runExclusive(async () => {
            const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
            const data = await externalInvoiceApi.getVendorOriginationStatistics<api.OriginationRunningStatistic>(params);
            return { data };
          });
        },
      }),
    getGrowthStatistics:
      build.query<api.GrowthStatisticsResponse, api.GrowthStatisticsFilterParams>({
        async queryFn(params) {
          return await StatisticsMutex.runExclusive(async () => {
            const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
            const data = await externalInvoiceApi.getGrowthStatistics<api.GrowthStatisticsResponse>(params);
            return { data };
          });
        },
      }),
    getGrossProductVolumeRunRate:
      build.query<api.GrossProductVolumeRunRateResponse | undefined, api.GrossProductVolumeRunRateParams>({
        async queryFn(params) {
          return await StatisticsMutex.runExclusive(async () => {
            const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
            const data = await externalInvoiceApi.getGrossProductVolumeRunRate<api.GrossProductVolumeRunRateResponse>(params);
            return { data };
          });
        },
      }),
    getInvoicePricingStatistics: build.query<api.InvoicePricingStatisticsResponse, api.InvoicePricingStatisticsParams>({
      async queryFn(params) {
        return await StatisticsMutex.runExclusive(async () => {
          const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
          const data = await externalInvoiceApi.getInvoicePricingStatistics(params);
          return { data };
        });
      },
    }),
    // Invoice Line Items
    getAllInvoiceLineItems: build.query<api.IPagedResults<api.InvoiceLineItemResponse>, api.InvoiceLineItemFilterParams>({
      async queryFn(params) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getAllInvoiceLineItems<api.InvoiceLineItemResponse>(params);
        return { data };
      },
    }),
    getInvoiceLineItems: build.query<readonly api.InvoiceLineItemResponse[], { ids: readonly string[] }>({
      async queryFn({ ids }, _queryApi, _extraOptions, fetchWithBQ) {
        const responses = await Promise.all(ids.map((id) => fetchWithBQ({
          url: `invoice-line-items/${id}`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        })));
        return {
          data: responses.map((r) => r.data as api.InvoiceLineItemResponse),
        };
      },
    }),
    amendInvoiceDueDate: build.mutation<api.InvoiceAggregateResponse | undefined, { id: string, newDueDate: string, skipInvalidation?: boolean }>({
      async queryFn({ id, skipInvalidation: _skipInvalidation, ...params }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.amendInvoiceDueDate<api.InvoiceAggregateResponse>(id, params);
        return { data };
      },
      invalidatesTags: (result, error, args) => (args.skipInvalidation
        ? []
        : result
          ? [{ type: 'Invoice', ...args, ...result }, 'Invoices', 'InvoiceAudit']
          : ['Invoice', 'Invoices']),
    }),
    amendInvoiceDate: build.mutation<api.InvoiceAggregateResponse | undefined, { id: string, newInvoiceDate: string, skipInvalidation?: boolean }>({
      async queryFn({ id, skipInvalidation: _skipInvalidation, ...params }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.amendInvoiceDate<api.InvoiceAggregateResponse>(id, params);
        return { data };
      },
      invalidatesTags: (result, error, args) => (args.skipInvalidation
        ? []
        : result
          ? [{ type: 'Invoice', ...args, ...result }, 'Invoices', 'InvoiceAudit']
          : ['Invoice', 'Invoices']),
    }),
    amendExternalClientId: build.mutation<api.InvoiceAggregateResponse | undefined, { id: string, newExternalClientId: string }>({
      async queryFn({ id, ...params }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.amendExternalClientId<api.InvoiceAggregateResponse>(id, params);
        return { data };
      },
      invalidatesTags: (result, error, args) => (result
        ? [{ type: 'Invoice', ...args, ...result }, 'Invoices', 'InvoiceAudit', 'InvoicesNeedingApproval']
        : ['Invoice', 'Invoices']),
    }),
    amendEligibilityDate: build.mutation<api.InvoiceAggregateResponse | undefined, { id: string, newEligibilityDate: string, skipInvalidation?: boolean }>({
      async queryFn({ id, skipInvalidation: _skipInvalidation, ...params }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.amendEligibilityDate<api.InvoiceAggregateResponse>(id, params);
        return { data };
      },
      invalidatesTags: (result, error, args) => (args.skipInvalidation
        ? []
        : result
          ? [{ type: 'Invoice', ...args, ...result }, 'Invoices', 'InvoiceAudit']
          : ['Invoice', 'Invoices']),
    }),
    uploadAmendEligibilityDate: build.mutation<void, { body: FormData }>({
      async queryFn({ body }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await (invoiceApi as any).post(`/invoices/amendEligibilityDate/upload`, body, {
          validateStatus: function (status: any) {
            return status >= 200 && status < 300; // default
          },
        });  // don't treat 404 as a success w/ undefined
        return { data };
      },
      invalidatesTags: ['Invoices', 'InvoiceLineItems'],
    }),
    amendLineItemDueDate: build.mutation<api.InvoiceLineItemResponse, { id: string, newDueDate: string }>({
      query(args) {
        return {
          url: `/invoice-line-items/${args.id}/amendDueDate`,
          method: 'POST',
          body: {
            newDueDate: args.newDueDate,
          },
        };
      },
      invalidatesTags: (result, error, id) => (result
        ? [{ ...id, ...result, type: 'InvoiceLineItem' }, 'Invoices', 'InvoiceLineItems', 'InvoiceAudit']
        : ['InvoiceLineItem', 'Invoices', 'InvoiceLineItems', 'InvoiceAudit']),
    }),
    cancelLineItem: build.mutation<api.InvoiceLineItemResponse | undefined, api.CancelInvoiceRequest & { id: string }>({
      async queryFn({ id, ...body }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.cancelInvoiceLineItem<api.InvoiceLineItemResponse>(id, body);
        return { data };
      },
      invalidatesTags: (result, error, id) => (result
        ? [{ ...id, ...result, type: 'InvoiceLineItem' }, 'Invoices', 'Invoice', 'InvoiceLineItems', 'InvoiceAudit']
        : ['InvoiceLineItem', 'Invoices', 'Invoice', 'InvoiceLineItems', 'InvoiceAudit']),
    }),
    amendLineItemEligibilityDate: build.mutation<api.MaybeRedactedInvoiceLineItemResponse | undefined, { id: string, newEligibilityDate: string }>({
      async queryFn({ id, ...params }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.amendLineItemEligibilityDate<api.InvoiceLineItemResponse>(id, params);
        return { data };
      },
      invalidatesTags: (result, error, id) => (result
        ? [{ ...id, ...result, type: 'InvoiceLineItem' }, 'Invoices', 'InvoiceLineItems', 'InvoiceAudit']
        : ['InvoiceLineItem', 'Invoices', 'InvoiceLineItems', 'InvoiceAudit']),
    }),
    getInvoiceLineItem: build.query<api.InvoiceLineItemResponse | undefined, { id: api.UUID }>({
      async queryFn({ id }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getInvoiceLineItem<api.InvoiceLineItemResponse>(id);
        return { data };
      },
      providesTags: (result, error, id) => (result
        ? [{ ...id, ...result, type: 'InvoiceLineItem' }, 'Invoices', 'InvoiceLineItems', 'InvoiceAudit']
        : ['InvoiceLineItem', 'Invoices', 'InvoiceLineItems', 'InvoiceAudit']),
    }),
    // Invoice Collections
    getInvoiceCollections: build.query<readonly api.InvoiceCollectionAllocationResponse[], api.InvoiceCollectionFilterParams>({
      async queryFn(params) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getAllInvoiceCollectionAllocations(params);
        return { data };
      },
    }),
    getInvoicesForCollection: build.query<api.IPagedResults<api.InvoiceAggregateResponse>, { collectionId: api.UUID } & api.InvoicesForCollectionFilterParams>({
      async queryFn({ collectionId, ...params }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getInvoicesForCollection<api.InvoiceAggregateResponse>(collectionId, params);
        return { data };
      },
      providesTags: (result, error, { collectionId }) => (result
        ? [{ collectionId, ...result, type: 'CollectionInvoices' }]
        : ['CollectionInvoices']),
    }),
    // Adjustments
    createAdjustmentForVendor: build.mutation<api.InvoiceAdjustmentResponse, { vendorId: string } & api.CreateVendorAdjustmentRequest>({
      async queryFn({ vendorId, ...request }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.createAdjustmentForVendor(vendorId, request);
        return { data };
      },
      invalidatesTags: ['InvoiceAdjustments', 'InvoiceLineItem', 'InvoiceLineItems', 'Invoice', 'Invoices'],
    }),
    getInvoiceAdjustment:
      build.query<api.InvoiceAdjustmentResponse, { id: api.UUID }>({
        async queryFn({ id }) {
          const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
          const data = await externalInvoiceApi.getAdjustment(id);
          return { data };
        },
        providesTags: ['InvoiceAdjustments'],
      }),
    patchAdjustment: build.mutation<api.InvoiceAdjustmentResponse, { id: string } & api.UpdateAdjustmentLineItemRequest>({
      async queryFn({ id, ...request }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.updateAdjustmentTargetLineItem(id, request);
        return { data };
      },
      invalidatesTags: ['InvoiceAdjustments', 'InvoiceLineItem', 'InvoiceLineItems', 'Invoice', 'Invoices'],
    }),
    getInvoiceAdjustments:
      build.query<readonly api.InvoiceAdjustmentResponse[], { id: api.UUID }>({
        async queryFn({ id }) {
          const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
          const data = await externalInvoiceApi.getAdjustmentsForInvoice<api.InvoiceAdjustmentResponse>(id);
          return { data };
        },
        providesTags: ['InvoiceAdjustments'],
      }),
    getVendorAdjustmentBalance:
      build.query<api.InvoiceAdjustmentBalanceForVendorResponse, { vendorId: string } & api.IPageFilterParams>({
        async queryFn({ vendorId }) {
          const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
          const data = await externalInvoiceApi.getAdjustmentBalanceForVendor<api.InvoiceAdjustmentBalanceForVendorResponse>(vendorId);
          return { data };
        },
        providesTags: (result, error, id) => (result
          ? [{ type: 'InvoiceAdjustments', ...id, ...result }]
          : ['InvoiceAdjustments']),
      }),
    getAllAdjustmentGroups:
      build.query<api.IPagedResults<api.InvoiceAdjustmentGroupResponse>, api.InvoiceAdjustmentGroupFilterParams>({
        async queryFn(params) {
          const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
          const data = await externalInvoiceApi.getAllAdjustmentGroups(params);
          return { data };
        },
        providesTags: ['AdjustmentGroups'],
      }),
    getAdjustmentGroup:
      build.query<api.InvoiceAdjustmentGroupWithAdjustmentsResponse | undefined, { id: string }>({
        async queryFn({ id }) {
          const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
          const data = await externalInvoiceApi.getAdjustmentGroup(id);
          return { data };
        },
        providesTags: (result, error, params) => (result
          ? [{ type: 'AdjustmentGroups', ...params, ...result }]
          : ['AdjustmentGroups']),
      }),
    approveAdjustmentGroup:
      build.mutation<api.InvoiceAdjustmentGroupWithAdjustmentsResponse | undefined, { groupId: string, skipInvalidation?: boolean }>({
        async queryFn({ groupId }) {
          const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
          const data = await externalInvoiceApi.approveAdjustmentGroup(groupId);
          return { data };
        },
        invalidatesTags: (result, _error, params) => (params.skipInvalidation ?
          []
          : result
            ? [{ ...params, ...result, type: 'AdjustmentGroups' }]
            : ['AdjustmentGroups']),
      }),
    getVendorsAdjustmentBalances:
      build.query<api.IPagedResults<api.InvoiceAdjustmentNetBalanceResponse>, api.InvoiceAdjustmentsFilterParams>({
        async queryFn(params) {
          const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
          const data = await externalInvoiceApi.getAdjustmentBalanceByVendor<api.InvoiceAdjustmentNetBalanceResponse>(params);
          return { data };
        },
      }),
    getCashflowProjections:
      build.query<api.CashFlowProjectionsResponse, api.GetCashFlowProjectionsFilterParams>({
        query({
          beginningAt,
          endingAt,
          periodization,
          startingBalance,
          includePendingApproval,
        }) {
          return {
            url: '/invoices/statistics/cashflowProjections',
            method: 'GET',
            params: {
              beginningAt,
              endingAt,
              periodization,
              startingBalance,
              includePendingApproval,
            },
            headers: {
              'Content-Type': 'application/json',
            },
          };
        },
      }),
    getTaskSchedules: build.query<Record<string, string>, any>({
      async queryFn() {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getTaskSchedules<Record<string, string>>();
        return { data };
      },
      providesTags: ['TaskSchedules'],
    }),
    getAllTaskExecutions: build.query<api.IPagedResults<api.InvoiceOperationsTaskExecutionResponse>, api.InvoiceOperationsTaskExecutionFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAllOperationsTaskExecutions(params);
        return { data };
      },
      providesTags: ['TaskExecutions'],
    }),
    createTaskExecution: build.mutation<api.InvoiceOperationsTaskExecutionResponse, api.InvoiceOperationsTaskExecutionRequest>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.createOperationsTaskExecution(params);
        return { data };
      },
      invalidatesTags: ['TaskExecutions'],
    }),
    uploadInvoices: build.mutation<void, { body: FormData }>({
      async queryFn({ body }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.uploadInvoices(body);
        return { data };
      },
      invalidatesTags: ['Invoices', 'InvoiceLineItems'],
    }),
    updatePaidInvoices: build.mutation<api.UpdatePaidInvoicesResponse, { body: FormData }>({
      async queryFn({ body }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.updatePaidInvoices(body);
        return { data };
      },
    }),
    checkEligibleInvoices: build.mutation<api.CheckEligibleInvoicesResponse, { body: FormData }>({
      async queryFn({ body }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.checkEligibleInvoices(body);
        return { data };
      },
    }),
    processInvoiceFile: build.mutation<api.ProcessInvoiceFileResponse, { body: FormData }>({
      async queryFn({ body }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.processInvoiceFile(body);
        return { data };
      },
      invalidatesTags: ['Invoice', 'Invoices', 'InvoiceLineItems', 'InvoiceAudit', 'InvoiceQuarantines'],
    }),
    uploadInvoiceLineItemsReceivableOwner: build.mutation<void, { body: FormData }>({
      async queryFn({ body }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.updateInvoiceLineItemsReceivableOwner(body);
        return { data };
      },
      invalidatesTags: ['Invoices', 'InvoiceLineItems'],
    }),
    uploadInvoiceLineItemsReceivableOwnerV2: build.mutation<void, { body: FormData }>({
      async queryFn({ body }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.updateInvoiceLineItemsReceivableOwnerV2(body);
        return { data };
      },
      invalidatesTags: ['Invoices', 'InvoiceLineItems'],
    }),
    getInvoiceAudit: build.query<api.IPagedResults<api.InvoicesAuditResponse>, api.InvoiceAuditFilterParams & { id: string; }>({
      async queryFn({ id, ...params }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getInvoiceAudit(id, params);
        return { data };
      },
      providesTags: ['InvoiceAudit'],
    }),
    getCreditTierPortfolio: build.query<api.CreditTierPortfolioResponse, api.CreditTierPortfolioRequest>({
      async queryFn(params) {
        return await StatisticsMutex.runExclusive(async () => {
          const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
          const data = await externalInvoiceApi.getCreditTierPortfolio(params);
          return { data };
        });
      },
    }),
    // Clients
    createClient: build.mutation<api.ClientResponse, api.ClientRequest>({
      async queryFn(params) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.createClient(params);
        return { data };
      },
      invalidatesTags: ['Clients'],
    }),
    getClient: build.query<api.ClientResponse | undefined, { id: string }>({
      async queryFn({ id }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getClient(id);
        return { data };
      },
      providesTags: (result, error, id) => (result
        ? [{ type: 'Client', ...id, ...result }]
        : ['Client']),
    }),
    getAllClients: build.query<api.IPagedResults<api.ClientListResponse>, api.ClientFilterParams>({
      async queryFn(params) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getAllClients(params) as unknown as api.IPagedResults<api.ClientListResponse>;
        return { data };
      },
      providesTags: ['Clients'],
    }),
    updateClient: build.mutation<api.ClientResponse | undefined, api.UpdateClientRequest & { id: api.UUID }>({
      async queryFn({ id, ...request }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.updateClient(id, request);
        return { data };
      },
      invalidatesTags: (result, error, { id }) => (result
        ? [{ id, type: 'Client' }, 'Clients']
        : []),
    }),
    deleteClient: build.mutation<api.ClientResponse | undefined, { id: api.UUID }>({
      async queryFn({ id }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.deleteClient(id);
        return { data };
      },
      invalidatesTags: (result, error, { id }) => (result
        ? [{ id, type: 'Client' }, 'Clients']
        : []),
    }),
    uploadConsolidatedClients: build.mutation<void, { body: FormData, partnerId: number }>({
      async queryFn({ body, partnerId }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.uploadConsolidatedClients(partnerId, body);
        return { data };
      },
      invalidatesTags: ['ClientMappings', 'ClientMapping'],
    }),
    uploadSnpIqCreditRatings: build.mutation<{ updatedCount: number; noMatchCount: number; noRatingCount: number; errorCount: number }, { body: FormData }>({
      async queryFn({ body }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await (invoiceApi as any).post(`/clients/snp-iq/upload`, body, {
          validateStatus: (status: any) => status >= 200 && status < 300,
        });
        return { data };
      },
      invalidatesTags: ['Clients', 'Client'],
    }),
    // Client Mappings
    createClientMapping: build.mutation<api.ExternalClientResponse, api.ExternalClientRequest & { partnerId: number, externalClientId: string }>({
      async queryFn({ partnerId, externalClientId, ...request }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.createExternalClient(partnerId, externalClientId, request);
        return { data };
      },
      invalidatesTags: (result, error, { clientId }) => (clientId ? [{ type: 'Client', id: clientId }, 'ClientMappings'] : ['ClientMappings']),
    }),
    getClientMapping: build.query<api.ExternalClientResponse | undefined, { partnerId: number, externalClientId: string }>({
      async queryFn({ partnerId, externalClientId }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getExternalClient(partnerId, externalClientId);
        return { data };
      },
      providesTags: (result, error, { partnerId, externalClientId }) => [{ type: 'ClientMapping', partnerId, externalClientId }],
    }),
    getAllClientMappings: build.query<api.IPagedResults<api.ExternalClientResponse>, api.ClientMappingFilterParams>({
      async queryFn(params) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getAllExternalClients(params);
        return { data };
      },
      providesTags: ['ClientMappings'],
    }),
    updateClientMapping: build.mutation<api.ExternalClientResponse | undefined, api.UpdateExternalClientRequest & { partnerId: number, externalClientId: string }>({
      async queryFn({ partnerId, externalClientId, ...request }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.updateExternalClient(partnerId, externalClientId, request);
        return { data };
      },
      invalidatesTags: (result, error, { partnerId, externalClientId }) => (result?.clientId ? [{ type: 'Client', id: result.clientId }, { type: 'ClientMapping', partnerId, externalClientId }] : [{ type: 'ClientMapping', partnerId, externalClientId }]),
    }),
    linkClientMapping: build.mutation<api.ExternalClientResponse | undefined, api.LinkExternalClientRequest & { partnerId: number, externalClientId: string }>({
      async queryFn({ partnerId, externalClientId, ...params }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.linkExternalClient(partnerId, externalClientId, params);
        return { data };
      },
      invalidatesTags: (result, error, { partnerId, externalClientId }) => (result
        ? [{ partnerId, externalClientId, type: 'ClientMapping' }, 'ClientMappings']
        : []),
    }),
    approveClientMapping: build.mutation<api.ExternalClientResponse | undefined, api.ApproveExternalClientRequest & { partnerId: number, externalClientId: string }>({
      async queryFn({ partnerId, externalClientId, ...request }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.approveExternalClient(partnerId, externalClientId, request);
        return { data };
      },
      invalidatesTags: (result, error, { partnerId, externalClientId }) => [{ type: 'ClientMapping', partnerId, externalClientId }],
    }),
    rejectClientMapping: build.mutation<api.ExternalClientResponse | undefined, api.RejectExternalClientRequest & { partnerId: number, externalClientId: string }>({
      async queryFn({ partnerId, externalClientId, ...request }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.rejectExternalClient(partnerId, externalClientId, request);
        return { data };
      },
      invalidatesTags: (result, error, { partnerId, externalClientId }) => [{ type: 'ClientMapping', partnerId, externalClientId }],
    }),
    unapproveClientMapping: build.mutation<api.ExternalClientResponse | undefined, api.UnapproveExternalClientRequest & { partnerId: number, externalClientId: string }>({
      async queryFn({ partnerId, externalClientId, ...request }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.unapproveExternalClient(partnerId, externalClientId, request);
        return { data };
      },
      invalidatesTags: (result, error, { partnerId, externalClientId }) => [{ type: 'ClientMapping', partnerId, externalClientId }],
    }),
    // Client Exclusions
    getAllClientExclusions: build.query<api.IPagedResults<api.ExternalClientResponse>, api.ExternalClientFilterParams>({
      async queryFn(params) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getAllExternalClients({ ...params, isExcluded: true });
        return { data };
      },
      providesTags: ['ClientExclusions'],
    }),
    createClientExclusion: build.mutation<api.ExternalClientResponse, api.ExternalClientExclusionRequest & { partnerId: number, externalClientId: string }>({
      async queryFn({ partnerId, externalClientId, ...request }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.createExternalClientExclusion(partnerId, externalClientId, request);
        return { data };
      },
      invalidatesTags: (result, error, { partnerId, externalClientId }) => ['ClientExclusions', { type: 'ClientMapping', partnerId, externalClientId }],
    }),
    deleteClientExclusion: build.mutation<api.ExternalClientResponse, { partnerId: number, externalClientId: string, skipInvalidation: boolean }>({
      async queryFn({ partnerId, externalClientId }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.deleteExternalClientExclusion(partnerId, externalClientId);
        return { data };
      },
      invalidatesTags: (result, error, { partnerId, externalClientId }) => ['ClientExclusions', { type: 'ClientMapping', partnerId, externalClientId }],
    }),
    // Client Mapping Notes
    createClientMappingNote: build.mutation<api.ExternalClientNoteResponse | undefined, api.ExternalClientNoteRequest & { partnerId: number, externalClientId: string }>({
      async queryFn({
        partnerId, externalClientId, ...request
      }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.createExternalClientNote(partnerId, externalClientId, request);
        return { data };
      },
      invalidatesTags: (result, error, { partnerId, externalClientId }) => [{ type: 'ClientMapping', partnerId, externalClientId }],
    }),
    deleteClientMappingNote: build.mutation<api.ExternalClientNoteResponse | undefined, { partnerId: number, externalClientId: string, noteId: string }>({
      async queryFn({ partnerId, externalClientId, noteId }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.deleteExternalClientNote(partnerId, externalClientId, noteId);
        return { data };
      },
      invalidatesTags: (result, error, { partnerId, externalClientId }) => [{ type: 'ClientMapping', partnerId, externalClientId }],
    }),
    uploadClientMappingOverrides: build.mutation<void, { body: FormData; partnerId: number; externalClientId: string }>({
      async queryFn({ body, partnerId, externalClientId }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.uploadExternalClientOverrides(partnerId, externalClientId, body);
        return { data };
      },
      invalidatesTags: (result, error, { partnerId, externalClientId }) => [{ type: 'ClientMapping', partnerId, externalClientId }],
    }),
    createClientMappingOverride: build.mutation<api.ExternalClientOverrideResponse | undefined, api.ExternalClientOverrideRequest & { partnerId: number, externalClientId: string, externalVendorId: string }>({
      async queryFn({
        partnerId, externalClientId, externalVendorId, ...request
      }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.createExternalClientOverride(partnerId, externalClientId, externalVendorId, request);
        return { data };
      },
      invalidatesTags: (result, error, { partnerId, externalClientId }) => [{ type: 'ClientMapping', partnerId, externalClientId }, 'ClientMappingOverrides'],
    }),
    patchClientMappingOverride: build.mutation<api.ExternalClientOverrideResponse | undefined, api.PatchExternalClientOverrideRequest & { partnerId: number, externalClientId: string, externalVendorId: string }>({
      async queryFn({
        partnerId, externalClientId, externalVendorId, ...request
      }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.patchExternalClientOverride(partnerId, externalClientId, externalVendorId, request);
        return { data };
      },
      invalidatesTags: (result, error, { partnerId, externalClientId }) => [{ type: 'ClientMapping', partnerId, externalClientId }, 'ClientMappingOverrides'],
    }),
    getAllClientMappingOverrides: build.query<api.IPagedResults<api.ExternalClientOverrideResponse>, api.ExternalClientOverrideFilterParams & { partnerId: api.Partners }>({
      async queryFn({ partnerId, ...params }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.getAllExternalClientOverrides(partnerId, params);
        return { data };
      },
      providesTags: ['ClientMappingOverrides'],
    }),
    deleteClientMappingOverride: build.mutation<api.ExternalClientOverrideResponse | undefined, { partnerId: number, externalClientId: string, externalVendorId: string }>({
      async queryFn({ partnerId, externalClientId, externalVendorId }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.deleteExternalClientOverride(partnerId, externalClientId, externalVendorId);
        return { data };
      },
      invalidatesTags: (result, error, { partnerId, externalClientId }) => [{ type: 'ClientMapping', partnerId, externalClientId }, 'ClientMappingOverrides'],
    }),
    // Vendor Client Restrictions
    uploadVendorClientRestrictions: build.mutation<void, { body: FormData, partnerId: api.Partners }>({
      async queryFn({ body, partnerId, }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.uploadVendorClientRestrictions(partnerId, body);
        return { data };
      },
      invalidatesTags: ['VendorClientRestrictions'],
    }),
    createVendorClientRestriction: build.mutation<api.VendorClientRestrictionResponse, { partnerId: api.Partners, externalVendorId: string, externalClientId: string } & api.VendorClientRestrictionRequest>({
      async queryFn({ partnerId, externalVendorId, externalClientId, ...request }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.createVendorClientRestriction(partnerId, externalVendorId, externalClientId, request);
        return { data };
      },
      invalidatesTags: (result, error, { partnerId, externalVendorId }) => [{ type: 'VendorClientRestrictions', partnerId, externalVendorId }],
    }),
    getVendorClientRestrictions: build.query<readonly api.VendorClientRestrictionResponse[], { partnerId: api.Partners, externalVendorIds: readonly string[] }>({
      async queryFn({ partnerId, externalVendorIds }, _queryApi, _extraOptions, fetchWithBQ) {
        const responses = await Promise.all(externalVendorIds.map((externalVendorId) => fetchWithBQ({
          url: `vendor-client-restrictions/partners/${partnerId}/external-vendors/${externalVendorId}`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        })));
        return {
          data: responses.flatMap((r) => r.data as readonly api.VendorClientRestrictionResponse[]).filter(vcr => !!vcr),
        };
      },
      providesTags: (result, error, { partnerId, externalVendorIds }) => externalVendorIds.map(externalVendorId => ({ type: 'VendorClientRestrictions', partnerId, externalVendorId })),
    }),
    getVendorClientRestrictionsForIdentifiers: build.query<readonly api.VendorClientRestrictionResponse[], { identifiers: readonly { partnerId: api.Partners; externalVendorId: string }[] }>({
      async queryFn({ identifiers }, _queryApi, _extraOptions, fetchWithBQ) {
        const responses = await Promise.all(identifiers.map(({ partnerId, externalVendorId }) => fetchWithBQ({
          url: `vendor-client-restrictions/partners/${partnerId}/external-vendors/${externalVendorId}`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        })));
        return {
          data: responses.flatMap((r) => r.data as readonly api.VendorClientRestrictionResponse[]).filter(vcr => !!vcr),
        };
      },
      providesTags: (result, error, { identifiers }) => identifiers.map(({ partnerId, externalVendorId }) => ({ type: 'VendorClientRestrictions' as const, partnerId, externalVendorId })),
    }),
    getAllVendorClientRestrictions: build.query<api.IPagedResults<api.VendorClientRestrictionResponse>, api.VendorClientRestrictionParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAllVendorClientRestrictions(params);
        return { data };
      },
      providesTags: ['VendorClientRestrictions'],
    }),
    deleteVendorClientRestriction: build.mutation<api.VendorClientRestrictionResponse, { partnerId: api.Partners, externalVendorId: string, externalClientId: string, skipInvalidation?: boolean }>({
      async queryFn({ partnerId, externalVendorId, externalClientId }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.deleteVendorClientRestriction(partnerId, externalVendorId, externalClientId);
        return { data };
      },
      invalidatesTags: (result, error, { partnerId, externalVendorId, skipInvalidation }) => skipInvalidation ? [] : [{ type: 'VendorClientRestrictions', partnerId, externalVendorId }],
    }),
    clearVendorCache: build.mutation<void, { vendorId: api.UUID }>({
      async queryFn({ vendorId }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.clearVendorCache(vendorId);
        return { data };
      },
    }),
    clearOperatorCache: build.mutation<void, { email: string }>({
      async queryFn({ email }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.clearOperatorCache(email);
        return { data };
      },
    }),
    //
    markBatchSweepPaid: build.mutation<api.BatchSweepResponse | undefined, api.MarkBatchSweepPaidRequest & { id: string }>({
      async queryFn({ id }) {
        const externalInvoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await externalInvoiceApi.markBatchSweepPaid(id);
        return { data };
      },
      invalidatesTags: (result, error, id) => (result
        ? [{ type: 'BatchSweep', ...id, ...result }]
        : ['BatchSweep']),
    }),
    getAllInvoiceDocuments: build.query<api.IPagedResults<api.InvoiceDocumentResponse>, { invoiceId: api.UUID } & api.IPageFilterParams>({
      async queryFn({ invoiceId, ...params }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAllInvoiceDocuments<api.InvoiceDocumentResponse>(invoiceId, params);
        return { data };
      },
      providesTags: (result, error, { invoiceId }) => (result
        ? [{ type: 'InvoiceDocuments', invoiceId }]
        : ['InvoiceDocuments']),
    }),
    getInvoiceDocument: build.query<api.InvoiceDocumentResponse | undefined, { invoiceId: api.UUID; documentId: api.UUID }>({
      async queryFn({ invoiceId, documentId }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getInvoiceDocument<api.InvoiceDocumentResponse>(invoiceId, documentId);
        return { data };
      },
      providesTags: (result, error, { invoiceId }) => (result
        ? [{ type: 'InvoiceDocuments', invoiceId }]
        : ['InvoiceDocuments']),
    }),
    markInvoiceDocumentVerified: build.mutation<api.InvoiceDocumentResponse | undefined, { invoiceId: api.UUID; documentId: api.UUID } & api.MarkInvoiceDocumentVerifiedRequest>({
      async queryFn({ invoiceId, documentId, ...request }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.markInvoiceDocumentVerified<api.InvoiceDocumentResponse>(invoiceId, documentId, request);
        return { data };
      },
      invalidatesTags: (result, error, { invoiceId }) => (result
        ? [{ type: 'InvoiceDocuments', invoiceId }]
        : ['InvoiceDocuments']),
    }),
    getAllConfigurationSettings: build.query<readonly api.InvoiceConfigurationSettingResponse[], void>({
      async queryFn() {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAllConfigurationSettings();
        return { data };
      },
      providesTags: ['ConfigurationSettings'],
    }),
    getPortfolioMonitoringConfigurationSettings: build.query<readonly api.InvoiceConfigurationSettingResponse[], void>({
      async queryFn() {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAllConfigurationSettings({ category: api.InvoiceConfigurationSettingCategory.PortfolioMonitoring });
        return { data };
      },
      providesTags: ['ConfigurationSettings'],
    }),
    updateConfigurationSetting: build.mutation<api.InvoiceConfigurationSettingResponse | undefined, { name: string; } & api.InvoiceConfigurationSettingRequest>({
      async queryFn({ name, ...request }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.updateConfigurationSetting(name, request);
        return { data };
      },
      invalidatesTags: ['ConfigurationSettings'],
    }),
    requestSqlQueryAnswer: build.mutation<api.SqlQuestionResponse, api.SqlQuestionRequest>({
      async queryFn(request) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.requestSqlQueryAnswer(request);
        return { data };
      },
    }),
    recalculateInvoiceFees: build.mutation<void, api.RecalculateInvoiceFeesRequest>({
      async queryFn(request) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.recalculateInvoiceFees(request);
        return { data };
      },
      invalidatesTags: ['Invoices'],
    }),
    reconfigureInvoice: build.mutation<api.InvoiceAggregateResponse | undefined, { id: api.UUID }>({
      async queryFn({ id }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.reconfigureInvoice<api.InvoiceAggregateResponse>(id);
        return { data };
      },
      invalidatesTags: (result, _error, { id }) => (result
        ? [{ type: 'Invoice', id }, 'Invoices', 'InvoiceAudit']
        : ['Invoice', 'Invoices', 'InvoiceAudit']),
    }),
    reconfigureInvoices: build.mutation<void, api.ReconfigureInvoicesRequest>({
      async queryFn(request) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.reconfigureInvoices(request);
        return { data };
      },
      invalidatesTags: ['Invoices'],
    }),
    setInvoiceFixedFee: build.mutation<api.InvoiceAggregateResponse | undefined, { id: api.UUID, skipInvalidation?: boolean } & api.InvoiceFixedFeeRequest>({
      async queryFn({ id, ...request }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.setInvoiceFixedFee<api.InvoiceAggregateResponse>(id, request);
        return { data };
      },
      invalidatesTags: (result, _error, args) => args.skipInvalidation
        ? []
        : (result ? [{ type: 'Invoice', ...args, ...result }] : ['Invoice']),
    }),
    getAcceleratedPayoutStatistics: build.query<api.AcceleratedPayoutStatisticsResponse, api.AcceleratedPayoutStatisticsParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAcceleratedPayoutStatistics(params);
        return { data };
      },
    }),
    getAcceleratedPayoutLookaheadBuckets: build.query<readonly api.AcceleratedPayoutLookaheadBucketResponse[], any>({
      async queryFn() {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAcceleratedPayoutLookaheadBuckets();
        return { data };
      },
    }),
    getEligibleInvoiceTotalsByVendor: build.query<api.IPagedResults<api.VendorEligibleInvoiceTotalsResponse>, api.EligibleInvoiceTotalsFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getEligibleInvoiceTotalsByVendor(params);
        return { data };
      },
    }),
    getVendorAllTimeInvoiceTotals: build.query<api.VendorBusinessTotalsResponse, { vendorId: api.UUID, partnerId?: api.Partners }>({
      async queryFn({ vendorId, partnerId }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getVendorAllTimeInvoiceTotals<api.VendorBusinessTotalsResponse>(vendorId, partnerId ? { partnerId } : undefined);
        return { data };
      },
    }),
    getInvoiceApprovalReasonSummary: build.query<api.InvoiceApprovalReasonSummaryResponse, api.InvoiceApprovalReasonSummaryFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getInvoiceApprovalReasonSummary(params);
        return { data };
      },
      providesTags: ['InvoicesNeedingApproval'],
    }),
    getInvoiceLineItemStateSummary: build.query<api.InvoiceLineItemStateSummary, api.InvoiceLineItemStateSummaryFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getInvoiceLineItemStateSummary(params);
        return { data };
      },
    }),
    getAllClientVendorAnomalies: build.query<api.IPagedResults<api.ClientVendorAnomalyResponse>, api.ClientVendorAnomalyFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAllClientVendorAnomalies(params);
        return { data };
      },
      providesTags: ['ClientVendorAnomalies'],
    }),
    getClientVendorAnomaly: build.query<api.ClientVendorAnomalyAggregateResponse, { id: api.UUID } & api.IPageFilterParams>({
      async queryFn({ id, ...params }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getClientVendorAnomaly(id, params);
        return { data };
      },
      providesTags: (result, error, { id }) => [{ type: 'ClientVendorAnomalies', id }],
    }),
    getAllInvoiceQuarantines: build.query<api.IPagedResults<api.InvoiceQuarantineWithInvoiceResponse>, api.InvoiceQuarantineFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAllInvoiceQuarantines(params);
        return { data };
      },
      providesTags: ['InvoiceQuarantines'],
    }),
    releaseInvoiceQuarantine: build.mutation<api.InvoiceQuarantineResponse, { id: api.UUID }>({
      async queryFn({ id }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.releaseInvoiceQuarantine(id);
        return { data };
      },
      // a failed release throws, which skips invalidation; the page refetches the list itself
      invalidatesTags: (result) => (result
        ? [{ type: 'Invoice', id: result.invoiceId }, 'Invoices', 'InvoiceAudit', 'InvoiceQuarantines']
        : []),
    }),
    getVendorInvoiceQuarantines: build.query<readonly api.RedactedVendorInvoiceQuarantineResponse[], { vendorId: api.UUID }>({
      async queryFn({ vendorId }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getVendorInvoiceQuarantines(vendorId);
        return { data };
      },
      providesTags: ['InvoiceQuarantines'],
    }),
    submitInvoiceQuarantineCorrections: build.mutation<readonly api.RedactedInvoiceQuarantineResponse[], api.SubmitInvoiceQuarantineCorrectionsRequest & { vendorId: api.UUID }>({
      async queryFn({ vendorId, ...request }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.submitVendorInvoiceQuarantineCorrections(vendorId, request);
        return { data };
      },
      // a failed submit throws, which skips invalidation; the page refetches the list itself
      invalidatesTags: (result) => (result
        ? [...result.map((q) => ({ type: 'Invoice' as const, id: q.invoiceId })), 'Invoices', 'InvoiceAudit', 'InvoiceQuarantines']
        : []),
    }),
    getInvoiceRevenueSeries: build.query<api.InvoiceRevenueSeriesResponse, api.InvoiceRevenueSeriesParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getInvoiceRevenueSeries(params);
        return { data };
      }
    }),
    getInvoiceRevenueByVendor: build.query<api.IPagedResults<api.InvoiceRevenueByBusinessEntity>, api.InvoiceRevenueByVendorParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getInvoiceRevenueByVendor(params);
        return { data };
      }
    }),
    getInvoiceRevenueByClient: build.query<api.IPagedResults<api.InvoiceRevenueByBusinessEntity>, api.InvoiceRevenueByClientParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getInvoiceRevenueByClient(params);
        return { data };
      }
    }),
    getInvoiceRevenueByPartner: build.query<api.IPagedResults<api.InvoiceRevenueByBusinessEntity>, api.InvoiceRevenueByPartnerParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getInvoiceRevenueByPartner(params);
        return { data };
      }
    }),
    getInvoiceCohortsSeries: build.query<api.InvoiceCohortsSeriesResponse, api.InvoiceCohortsSeriesParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getInvoiceCohortsSeries(params);
        return { data };
      }
    }),
    getInvoiceCohortVendorsSeries: build.query<api.IPagedResults<api.InvoiceCohortVendorSeriesResponse>, { cohort: api.ISODateString } & api.InvoiceCohortVendorsSeriesParams>({
      async queryFn({ cohort, ...params }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getInvoiceCohortVendorsSeries(cohort, params);
        return { data };
      }
    }),
    getPartnerIntegrationMetadata: build.query<api.PartnerIntegrationMetadataResponse, api.PartnerIntegrationMetadataDataParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getPartnerIntegrationMetadata(params);
        return { data };
      },
    }),
    getPartnerIntraPeriodInvoiceSeries: build.query<api.PartnerPeriodicInvoiceImportSummaryResponse, api.PartnerPeriodicInvoiceImportSummaryParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getPartnerIntraPeriodInvoiceSeries(params);
        return { data };
      },
    }),
    getOnBalanceSheetFundingLimitUtilization: build.query<api.OnBalanceSheetFundingLimitUtilizationResponse, void>({
      async queryFn() {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getOnBalanceSheetFundingLimitUtilization();
        return { data };
      },
    }),
    getExternalClientOverrideExposureLimitUtilization: build.query<api.IPagedResults<api.ExternalClientOverrideExposureLimitUtilizationResponse>, api.ExternalClientOverrideExposureLimitUtilizationFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getExternalClientOverrideExposureLimitUtilization(params);
        return { data };
      },
    }),
    getDueDateAdjustmentAlgorithmErrorMetadata: build.query<api.ExternalClientDueDateAdjustmentAlgorithmErrorMetadataResponse | undefined, { partnerId: number, externalClientId: string }>({
      async queryFn({ partnerId, externalClientId }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getDueDateAdjustmentAlgorithmErrorMetadata(partnerId, externalClientId);
        return { data };
      },
    }),
    getPartnerIntegrationComparisonBreakdown: build.query<api.PartnerIntegrationComparisonBreakdownPagedResults<api.PartnerIntegrationComparisonBreakdownByBusinessEntityResponse>, api.PartnerIntegrationComparisonBreakdownParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getPartnerIntegrationComparisonBeakdown(params);
        return { data };
      },
    }),
    getPartnerIntegrationFiles: build.query<api.IPagedResults<api.PartnerIntegrationFileResponse>, api.PartnerIntegrationFileFilterParams | void>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAllPartnerIntegrationFiles(params || undefined);
        return { data };
      },
    }),
    getScheduledVendorCollections: build.query<api.IPagedResults<api.ScheduledVendorCollectionResponse>, api.ScheduledVendorCollectionFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAllScheduledVendorCollections<api.ScheduledVendorCollectionResponse>(params);
        return { data };
      },
      providesTags: (result, error, { vendorId }) => [{ type: 'ScheduledVendorCollections', vendorId }],
    }),
    getAllScheduledVendorCollections: build.query<api.IPagedResults<api.ScheduledVendorCollectionResponse>, api.ScheduledVendorCollectionFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAllScheduledVendorCollections<api.ScheduledVendorCollectionResponse>(params);
        return { data };
      },
      providesTags: (result) => result ? result.data.map(d => ({ type: 'ScheduledVendorCollections', vendorId: d.vendorId })) : ['ScheduledVendorCollections'],
    }),
    getScheduledVendorCollection: build.query<api.ScheduledVendorCollectionResponse | undefined, { identifier: string }>({
      async queryFn({ identifier }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getScheduledVendorCollection<api.ScheduledVendorCollectionResponse>(identifier);
        return { data };
      },
      // `id` (not `vendorId`) so this is targetable on its own - see postponeScheduledVendorCollection
      providesTags: (result, error, { identifier }) => [{ type: 'ScheduledVendorCollections', id: identifier }],
    }),
    getScheduledVendorCollectionInvoiceCollections: build.query<api.IPagedResults<api.ScheduledInvoiceCollectionResponse> | undefined, { identifier: string } & api.ScheduledInvoiceCollectionFilterParams>({
      async queryFn({ identifier, ...params }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getScheduledVendorCollectionInvoiceCollections<api.ScheduledInvoiceCollectionResponse>(identifier, params);
        return { data };
      },
      // NOTE: must be `id`, not an arbitrary key like `identifier` - RTK Query only reads `id` for
      // scoping cache invalidation, so a non-`id` key is silently ignored and the tag falls back to
      // "invalidate every query of this type" (see postponeScheduledVendorCollection below, which used
      // to trip this on every vendor collection's invoice-collections list, causing a stale-identifier
      // refetch -> 404).
      providesTags: (result, error, { identifier }) => [{ type: 'ScheduledInvoiceCollections', id: identifier }],
    }),
    getInvoiceScheduledInvoiceCollections: build.query<api.IPagedResults<api.ScheduledInvoiceCollectionResponse> | undefined, { invoiceId: string } & api.ScheduledInvoiceCollectionFilterParams>({
      async queryFn({ invoiceId, ...params }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getInvoiceScheduledInvoiceCollections<api.ScheduledInvoiceCollectionResponse>(invoiceId, params);
        return { data };
      },
      providesTags: (result, error, { invoiceId }) => [{ type: 'ScheduledInvoiceCollections', id: invoiceId }],
    }),
    getScheduledVendorCollectionTransactionMatches: build.query<api.ScheduledVendorCollectionTransactionMatchesResponse, { identifier: string }>({
      async queryFn({ identifier }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getScheduledVendorCollectionTransactionMatches<api.ScheduledVendorCollectionTransactionMatchesResponse>(identifier);
        return { data };
      },
      providesTags: ['TransactionMatches'],
    }),
    getScheduledVendorCollectionTransactionMatchesV2: build.query<api.ScheduledVendorCollectionTransactionMatchesResponseV2, { identifier: string } & api.ScheduledVendorCollectionTransactionMatchFilterParams>({
      async queryFn({ identifier, ...params }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getScheduledVendorCollectionTransactionMatchesV2(identifier, params);
        return { data };
      },
      providesTags: ['TransactionMatches'],
    }),
    postponeScheduledVendorCollection: build.mutation<api.ScheduledVendorCollectionResponse | undefined, { identifier: string } & api.PostponeScheduledVendorCollectionRequest>({
      async queryFn({ identifier, ...params }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.postponeScheduledVendorCollection<api.ScheduledVendorCollectionResponse>(identifier, params);
        return { data };
      },
      invalidatesTags: (result, _error, { invoiceIds }) => (result
        ? [
          ...((invoiceIds || []).flatMap(invoiceId => [{ type: 'Invoice', invoiceId }, 'Invoices', 'InvoiceAudit', 'InvoicesNeedingApproval', { type: 'ScheduledInvoiceCollections', id: invoiceId }]) as any[]),
          { type: 'ScheduledVendorCollections', vendorId: result.vendorId },
          // Tag by the *response's* identifier, not the request's: when every invoice in the
          // collection is postponed together, that identifier now points at the group's new
          // collection date (see ScheduledCollectionService#postponeScheduledVendorCollection).
          // Invalidating the request's original identifier here would refetch a group that the
          // service just emptied out and 404 (`No scheduled vendor collection found for unique key`).
          { type: 'ScheduledVendorCollections', id: result.identifier },
          { type: 'ScheduledInvoiceCollections', id: result.identifier },
        ]
        : ['Invoice', 'Invoices', 'InvoiceAudit', 'InvoicesNeedingApproval', 'ScheduledVendorCollections', 'ScheduledInvoiceCollections']),
    }),
    collectScheduledVendorCollection: build.mutation<api.ScheduledVendorCollectionResponse | undefined, { identifier: string } & api.CollectScheduledVendorCollectionRequest>({
      async queryFn({ identifier, ...params }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.collectScheduledVendorCollection<api.ScheduledVendorCollectionResponse>(identifier, params);
        return { data };
      },
      // Collect Now requests every invoice in the collection (or the selected ones), and may move them to the settlement
      //  date - so, as with postponement, tag the collection by the *response's* identifier. The request's identifier
      //  isn't tagged: it may have been emptied (404). The page refetches it when selected invoices leave others behind.
      //  A failure is all-or-nothing on the server (nothing changed), so there's nothing to invalidate.
      invalidatesTags: (result) => (result
        ? [
          'Invoice', 'Invoices', 'InvoiceAudit', 'InvoicesNeedingApproval', 'ScheduledInvoiceCollections', 'ReconciliationPayloads',
          { type: 'ScheduledVendorCollections', vendorId: result.vendorId },
          { type: 'ScheduledVendorCollections', id: result.identifier },
        ]
        : []),
    }),
    modifyInvoiceScheduledInvoiceCollections: build.mutation<api.IPagedResults<api.ScheduledInvoiceCollectionResponse> | undefined, api.ModifyInvoiceScheduledInvoiceCollectionsRequest & { readonly invoiceId: api.UUID }>({
      async queryFn({ invoiceId, ...params }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.modifyInvoiceScheduledInvoiceCollections<api.ScheduledInvoiceCollectionResponse>(invoiceId, params);
        return { data };
      },
      invalidatesTags: (result, _error, { invoiceId }) => (result
        ? [{ type: 'Invoice', invoiceId }, 'Invoices', 'InvoiceAudit', 'InvoicesNeedingApproval', { type: 'ScheduledVendorCollections', vendorId: result.data[0]?.vendorId }, { type: 'ScheduledInvoiceCollections', id: invoiceId }]
        : ['Invoice', 'Invoices', 'InvoiceAudit', 'InvoicesNeedingApproval', 'ScheduledVendorCollections', 'ScheduledInvoiceCollections']),
    }),
    openInvoiceForFunding: build.mutation<api.InvoiceAggregateResponse | undefined, { id: api.UUID }>({
      async queryFn({ id }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.openInvoiceForFunding<api.InvoiceAggregateResponse>(id);
        return { data };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Invoices', id }],
    }),
    reconfigureOnDemandInFlight: build.mutation<api.InvoiceAggregateResponse | undefined, { id: api.UUID; skipInvalidation?: boolean }>({
      async queryFn({ id }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.reconfigureOnDemandInFlight<api.InvoiceAggregateResponse>(id);
        return { data };
      },
      invalidatesTags: (result, _error, arg) => (arg.skipInvalidation
        ? []
        : result
          ? [{ type: 'Invoice', ...arg, ...result }, 'Invoices', 'InvoiceAudit']
          : ['Invoice', 'Invoices', 'InvoiceAudit']),
    }),
    globalSearch: build.query<api.InvoiceGlobalSearchResponse, api.GlobalSearchFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.globalSearch(params);
        return { data };
      },
    }),
    getScheduledCollectionsAggregateStats: build.query<api.ScheduledVendorCollectionsAggregateStatisticsResponse, api.ScheduledCollectionsAggregateStatsFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getScheduledCollectionsAggregateStats<api.ScheduledVendorCollectionsAggregateStatisticsResponse>(params);
        return { data };
      },
    }),
    getScheduledVendorCollectionsSummary: build.query<api.ScheduledVendorCollectionsSummaryResponse, api.ScheduledVendorCollectionsSummaryFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getScheduledVendorCollectionsSummary<api.ScheduledVendorCollectionsSummaryResponse>(params);
        return { data };
      },
    }),
    getScheduledInvoiceCollectionsTimeSeries: build.query<api.ScheduledInvoiceCollectionsTimeSeriesResponse, api.ScheduledCollectionsTimeSeriesFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getScheduledInvoiceCollectionsTimeSeries<api.ScheduledInvoiceCollectionsTimeSeriesResponse>(params);
        return { data };
      },
    }),
    getAllVendorTransfers: build.query<api.IPagedResults<api.VendorTransferResponse>, api.VendorTransferFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAllVendorTransfers<api.VendorTransferResponse>(params);
        return { data };
      },
    }),
    getCollectionMatchSignals: build.query<api.IPagedResults<api.CollectionMatchSignalResponse>, api.CollectionMatchSignalFilterParams | void>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getCollectionMatchSignals<api.CollectionMatchSignalResponse>(params || undefined);
        return { data };
      },
      providesTags: ['CollectionMatchSignals'],
    }),
    getCollectionMatchSignalsByCollection: build.query<readonly api.CollectionMatchSignalResponse[], api.UUID>({
      async queryFn(identifier) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getCollectionMatchSignalsByCollection<api.CollectionMatchSignalResponse>(identifier);
        return { data };
      },
      providesTags: ['CollectionMatchSignals'],
    }),
    dismissCollectionMatchSignal: build.mutation<api.CollectionMatchSignalResponse, api.UUID>({
      async queryFn(signalId) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.dismissCollectionMatchSignal<api.CollectionMatchSignalResponse>(signalId);
        return { data };
      },
      invalidatesTags: ['CollectionMatchSignals'],
    }),
    getAprDashboard: build.query<api.AprDashboardResponse, void>({
      async queryFn() {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAprDashboard<api.AprDashboardResponse>() as api.AprDashboardResponse;
        return { data };
      },
      providesTags: ['AprDashboard'],
    }),
    getAprSummary: build.query<api.AprDashboardSummary, void>({
      async queryFn() {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAprSummary<api.AprDashboardSummary>() as api.AprDashboardSummary;
        return { data };
      },
      providesTags: ['AprDashboard'],
    }),
    getAprVendors: build.query<api.AprListResponse, api.AprQueryParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAprVendors<api.AprListResponse>(params) as api.AprListResponse;
        return { data };
      },
      providesTags: ['AprDashboard'],
    }),
    getAprClients: build.query<api.AprListResponse, api.AprQueryParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAprClients<api.AprListResponse>(params) as api.AprListResponse;
        return { data };
      },
      providesTags: ['AprDashboard'],
    }),
    getAprPartners: build.query<api.AprListResponse, api.AprQueryParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAprPartners<api.AprListResponse>(params) as api.AprListResponse;
        return { data };
      },
      providesTags: ['AprDashboard'],
    }),
    getAprVendorClientCombinations: build.query<api.AprVendorClientListResponse, api.AprVendorClientQueryParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAprVendorClientCombinations<api.AprVendorClientListResponse>(params) as api.AprVendorClientListResponse;
        return { data };
      },
      providesTags: ['AprDashboard'],
    }),
    // External-client grain (ar_external_client_agg_dashboard, MESA-4678/B12) — one row per
    // (partnerId, externalClientId) pair, since a buyer's exposure limit is set per funding
    // partner (external_client_mappings' PK). This is the grain the Limits page's buyer-limit
    // panel and "Buyer limits Σ" KPI need, not the deduped client grain from getAprClients.
    getAprExternalClients: build.query<api.AprListResponse, api.AprExternalClientQueryParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAprExternalClients<api.AprListResponse>(params) as api.AprListResponse;
        return { data };
      },
      providesTags: ['AprDashboard'],
    }),
    listTransactionMatches: build.query<api.IPagedResults<api.TransactionMatchResponse>, api.TransactionMatchFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.listTransactionMatches<api.TransactionMatchResponse>(params);
        return { data };
      },
      providesTags: ['TransactionMatches'],
    }),
    getTransactionMatch: build.query<api.TransactionMatchAggregateResponse, { id: api.UUID }>({
      async queryFn({ id }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getTransactionMatch<api.TransactionMatchAggregateResponse>(id);
        return { data };
      },
      providesTags: ['TransactionMatches'],
    }),
    confirmTransactionMatch: build.mutation<api.TransactionMatchResponse, { id: api.UUID }>({
      async queryFn({ id }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.confirmTransactionMatch<api.TransactionMatchResponse>(id);
        return { data };
      },
      invalidatesTags: MatchingMutationInvalidations,
    }),
    rejectTransactionMatch: build.mutation<api.TransactionMatchResponse, { id: api.UUID; reason?: string }>({
      async queryFn({ id, reason }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.rejectTransactionMatch<api.TransactionMatchResponse>(id, reason ? { reason } : undefined);
        return { data };
      },
      invalidatesTags: MatchingMutationInvalidations,
    }),
    confirmTransactionMatchGroup: build.mutation<api.TransactionMatchResponse[], { matchGroupId: api.UUID }>({
      async queryFn({ matchGroupId }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.confirmTransactionMatchGroup<api.TransactionMatchResponse>(matchGroupId);
        return { data };
      },
      invalidatesTags: MatchingMutationInvalidations,
    }),
    rejectTransactionMatchGroup: build.mutation<api.TransactionMatchResponse[], { matchGroupId: api.UUID; reason?: string }>({
      async queryFn({ matchGroupId, reason }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.rejectTransactionMatchGroup<api.TransactionMatchResponse>(matchGroupId, reason ? { reason } : undefined);
        return { data };
      },
      invalidatesTags: MatchingMutationInvalidations,
    }),
    createTransactionMatch: build.mutation<api.TransactionMatchResponse, api.CreateOperatorMatchRequest>({
      async queryFn(request) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.createTransactionMatch<api.TransactionMatchResponse>(request);
        return { data };
      },
      invalidatesTags: MatchingMutationInvalidations,
    }),
    listTransactionMatchingParams: build.query<api.TransactionMatchingParamsListResponse, api.TransactionMatchingParamsFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.listTransactionMatchingParams(params);
        return { data };
      },
      providesTags: ['TransactionMatchingParams'],
    }),
    getTransactionMatchingParams: build.query<api.TransactionMatchingParamsWithHistoryResponse, { id: api.UUID }>({
      async queryFn({ id }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getTransactionMatchingParams(id);
        return { data };
      },
      providesTags: ['TransactionMatchingParams'],
    }),
    upsertTransactionMatchingParams: build.mutation<api.TransactionMatchingParamsResponse, api.UpsertTransactionMatchingParamsRequest>({
      async queryFn(request) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.upsertTransactionMatchingParams(request);
        return { data };
      },
      invalidatesTags: ['TransactionMatchingParams'],
    }),
    deleteTransactionMatchingParams: build.mutation<void, { id: api.UUID }>({
      async queryFn({ id }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        await invoiceApi.deleteTransactionMatchingParams(id);
        return { data: undefined };
      },
      invalidatesTags: ['TransactionMatchingParams'],
    }),
    previewTransactionMatchingParamsForScope: build.query<Record<string, unknown>, api.PreviewTransactionMatchingParamsScopeParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.previewTransactionMatchingParamsForScope(params);
        return { data };
      },
    }),
    previewResolvedVendorMatchingParams: build.query<api.ResolvedVendorMatchingParamsResponse, { vendorId: api.UUID }>({
      async queryFn({ vendorId }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.previewResolvedVendorMatchingParams(vendorId);
        return { data };
      },
    }),
    getFactoringCollectionStatistics: build.query<api.IPagedResults<api.VendorFactoringCollectionStatisticsResponse>, api.VendorFactoringCollectionStatisticsFilterParams>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getFactoringCollectionStatistics<api.VendorFactoringCollectionStatisticsResponse>(params);
        return { data };
      },
    }),
    getAllReportMetadata: build.query<api.ReportMetadataListResponse, void>({
      async queryFn() {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAllReportMetadata();
        return { data };
      },
      providesTags: ['ReportMetadata'],
    }),
    listTransactionMatchingRuns: build.query<api.TransactionMatchingRunListResponse, api.TransactionMatchingRunFilterParams | void>({
      async queryFn(params) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.listTransactionMatchingRuns(params ?? undefined);
        return { data };
      },
      providesTags: ['TransactionMatchingRuns'],
    }),
    getTransactionMatchingRun: build.query<api.TransactionMatchingRunResponse, { id: api.UUID }>({
      async queryFn({ id }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getTransactionMatchingRun(id);
        return { data };
      },
      providesTags: ['TransactionMatchingRuns'],
    }),
    triggerTransactionMatchingRun: build.mutation<api.TransactionMatchingRunResponse, api.TriggerTransactionMatchingRunRequest>({
      async queryFn(request) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.triggerTransactionMatchingRun(request);
        return { data };
      },
      invalidatesTags: MatchingMutationInvalidations,
    }),
    getTransactionMatchGroup: build.query<readonly api.TransactionMatchResponse[], { matchGroupId: api.UUID }>({
      async queryFn({ matchGroupId }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getTransactionMatchGroup(matchGroupId);
        return { data };
      },
      providesTags: ['TransactionMatches'],
    }),
  }),
});

export const {
  useGetInvoiceQuery,
  useGetAllInvoicesQuery,
  useGetVendorInvoiceClientsQuery,
  useLazyGetAllInvoicesQuery,
  useUploadReconciliationPayloadMutation,
  useGetAllReconciliationPayloadsQuery,
  useGetReconciliationPayloadAggregateQuery,
  useAssociateReconciliationPayloadWithCollectionMutation,
  useApproveInvoiceMutation,
  useApproveInvoiceLineItemMutation,
  useUnapproveInvoiceMutation,
  useCancelInvoiceMutation,
  useUpdateInvoiceCollectionSchemeMutation,
  useRequestInvoiceRedirectMutation,
  useReopenInvoiceForRedirectMutation,
  useRequestInvoiceFundingMutation,
  useMakeInvoiceNotFundedMutation,
  useSendInvoiceUploadRequestedEmailMutation,
  useSendVoidedEmailMutation,
  useGetAllInvoiceLineItemsQuery,
  useGetInvoiceLineItemQuery,
  useGetInvoiceCollectionsQuery,
  useGetInvoicesForCollectionQuery,
  useCreateAdjustmentForVendorMutation,
  useGetInvoiceAdjustmentQuery,
  useGetInvoiceAdjustmentsQuery,
  usePatchAdjustmentMutation,
  useGetVendorsAdjustmentBalancesQuery,
  useGetCashflowProjectionsQuery,
  useLazyGetCashflowProjectionsQuery,
  useGetTaskSchedulesQuery,
  useGetAllTaskExecutionsQuery,
  useCreateTaskExecutionMutation,
  useAmendInvoiceDueDateMutation,
  useAmendInvoiceDateMutation,
  useAmendExternalClientIdMutation,
  useAmendLineItemDueDateMutation,
  useGetOriginationStatisticsQuery,
  useLazyGetOriginationStatisticsQuery,
  useGetGrowthStatisticsQuery,
  useLazyGetGrowthStatisticsQuery,
  useGetInvoicePricingStatisticsQuery,
  useLazyGetInvoicePricingStatisticsQuery,
  useGetGrossProductVolumeRunRateQuery,
  useLazyGetGrossProductVolumeRunRateQuery,
  useUploadInvoicesMutation,
  useUpdatePaidInvoicesMutation,
  useCheckEligibleInvoicesMutation,
  useProcessInvoiceFileMutation,
  useUploadInvoiceLineItemsReceivableOwnerMutation,
  useUploadInvoiceLineItemsReceivableOwnerV2Mutation,
  useGetInvoiceAuditQuery,
  useTakeSuggestedDueDateMutation,
  useGetCreditTierPortfolioQuery,
  useLazyGetCreditTierPortfolioQuery,
  useGetAllAdjustmentGroupsQuery,
  useGetAdjustmentGroupQuery,
  useApproveAdjustmentGroupMutation,
  useCreateClientMutation,
  useGetClientQuery,
  useGetAllClientsQuery,
  useLazyGetAllClientsQuery,
  useUpdateClientMutation,
  useDeleteClientMutation,
  useUploadConsolidatedClientsMutation,
  useUploadSnpIqCreditRatingsMutation,
  useCreateClientMappingNoteMutation,
  useDeleteClientMappingNoteMutation,
  useCreateClientMappingMutation,
  useGetClientMappingQuery,
  useGetAllClientMappingsQuery,
  useLazyGetAllClientMappingsQuery,
  useGetAllClientMappingOverridesQuery,
  useUpdateClientMappingMutation,
  useLinkClientMappingMutation,
  useApproveClientMappingMutation,
  useRejectClientMappingMutation,
  useUnapproveClientMappingMutation,
  useGetAllClientExclusionsQuery,
  useCreateClientExclusionMutation,
  useDeleteClientExclusionMutation,
  useUploadClientMappingOverridesMutation,
  useCreateClientMappingOverrideMutation,
  usePatchClientMappingOverrideMutation,
  useDeleteClientMappingOverrideMutation,
  useUploadVendorClientRestrictionsMutation,
  useCreateVendorClientRestrictionMutation,
  useGetVendorClientRestrictionsQuery,
  useGetVendorClientRestrictionsForIdentifiersQuery,
  useGetAllVendorClientRestrictionsQuery,
  useDeleteVendorClientRestrictionMutation,
  useClearVendorCacheMutation,
  useClearOperatorCacheMutation,
  useGetAllBatchSweepsQuery,
  useGetBatchSweepQuery,
  useGetRepaymentItemsForBatchSweepQuery,
  useCombineReconcilationPayloadsMutation,
  useMarkBatchSweepPaidMutation,
  useAmendEligibilityDateMutation,
  useUploadAmendEligibilityDateMutation,
  useAmendLineItemEligibilityDateMutation,
  useGetAllInvoiceDocumentsQuery,
  useGetInvoiceDocumentQuery,
  useMarkInvoiceDocumentVerifiedMutation,
  useGetAllConfigurationSettingsQuery,
  useGetPortfolioMonitoringConfigurationSettingsQuery,
  useUpdateConfigurationSettingMutation,
  useCancelLineItemMutation,
  useRequestSqlQueryAnswerMutation,
  useRecalculateInvoiceFeesMutation,
  useSetInvoiceFixedFeeMutation,
  useGetAcceleratedPayoutStatisticsQuery,
  useGetEligibleInvoiceTotalsByVendorQuery,
  useGetVendorAllTimeInvoiceTotalsQuery,
  useGetInvoiceApprovalReasonSummaryQuery,
  useGetInvoiceLineItemStateSummaryQuery,
  useGetAllClientVendorAnomaliesQuery,
  useGetClientVendorAnomalyQuery,
  useGetAllInvoiceQuarantinesQuery,
  useReleaseInvoiceQuarantineMutation,
  useGetVendorInvoiceQuarantinesQuery,
  useSubmitInvoiceQuarantineCorrectionsMutation,
  useGetInvoiceRevenueSeriesQuery,
  useGetInvoiceRevenueByVendorQuery,
  useGetInvoiceRevenueByClientQuery,
  useGetInvoiceRevenueByPartnerQuery,
  useGetInvoiceCohortsSeriesQuery,
  useLazyGetInvoiceCohortsSeriesQuery,
  useGetInvoiceCohortVendorsSeriesQuery,
  useLazyGetInvoiceCohortVendorsSeriesQuery,
  useGetAcceleratedPayoutLookaheadBucketsQuery,
  useDeleteReconciliationPayloadMutation,
  useGetPartnerIntegrationMetadataQuery,
  useGetPartnerIntraPeriodInvoiceSeriesQuery,
  useGetDueDateAdjustmentAlgorithmErrorMetadataQuery,
  useGetPartnerIntegrationComparisonBreakdownQuery,
  useGetPartnerIntegrationFilesQuery,
  useGetOnBalanceSheetFundingLimitUtilizationQuery,
  useGetExternalClientOverrideExposureLimitUtilizationQuery,
  useLazyGetPartnerIntegrationComparisonBreakdownQuery,
  useGetScheduledVendorCollectionsQuery,
  useGetAllScheduledVendorCollectionsQuery,
  useGetScheduledVendorCollectionQuery,
  useGetScheduledVendorCollectionInvoiceCollectionsQuery,
  useGetScheduledVendorCollectionTransactionMatchesQuery,
  useGetScheduledVendorCollectionTransactionMatchesV2Query,
  useGetInvoiceScheduledInvoiceCollectionsQuery,
  usePostponeScheduledVendorCollectionMutation,
  useCollectScheduledVendorCollectionMutation,
  useModifyInvoiceScheduledInvoiceCollectionsMutation,
  useGetScheduledCollectionsAggregateStatsQuery,
  useGetScheduledVendorCollectionsSummaryQuery,
  useGetScheduledInvoiceCollectionsTimeSeriesQuery,
  useOpenInvoiceForFundingMutation,
  useReconfigureOnDemandInFlightMutation,
  useReconfigureInvoiceMutation,
  useReconfigureInvoicesMutation,
  useGlobalSearchQuery,
  useGetAllVendorTransfersQuery,
  useGetCollectionMatchSignalsQuery,
  useGetCollectionMatchSignalsByCollectionQuery,
  useDismissCollectionMatchSignalMutation,
  useGetAprDashboardQuery,
  useGetAprSummaryQuery,
  useGetAprVendorsQuery,
  useGetAprClientsQuery,
  useGetAprPartnersQuery,
  useGetAprVendorClientCombinationsQuery,
  useGetAprExternalClientsQuery,
  useListTransactionMatchesQuery,
  useGetTransactionMatchQuery,
  useConfirmTransactionMatchMutation,
  useRejectTransactionMatchMutation,
  useConfirmTransactionMatchGroupMutation,
  useRejectTransactionMatchGroupMutation,
  useCreateTransactionMatchMutation,
  useListTransactionMatchingParamsQuery,
  useGetTransactionMatchingParamsQuery,
  useUpsertTransactionMatchingParamsMutation,
  useDeleteTransactionMatchingParamsMutation,
  useLazyPreviewTransactionMatchingParamsForScopeQuery,
  useLazyPreviewResolvedVendorMatchingParamsQuery,
  useGetFactoringCollectionStatisticsQuery,
  useGetAllReportMetadataQuery,
  useListTransactionMatchingRunsQuery,
  useGetTransactionMatchingRunQuery,
  useTriggerTransactionMatchingRunMutation,
  useGetTransactionMatchGroupQuery,
} = invoicesApi;

export const exportAllVendorClientRestrictions = async (filterParams?: api.VendorClientRestrictionParams): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.invoiceUrl}/vendor-client-restrictions/csv/export`);
  appendFilterParams(url, filterParams);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportAllInvoices = async (filterParams?: InvoiceFilterParams, excludeStates?: string[], csvFormat = CsvFormat.MESA_CSV_FORMAT, exportFormat: 'vendor' | 'operator' = 'operator'): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.invoiceUrl}/invoices/csv/export`);
  appendFilterParams(url, filterParams);

  (excludeStates || []).forEach((state) => {
    url.searchParams.append('excludeStates', state);
  });

  url.searchParams.append('csvFormat', csvFormat);

  url.searchParams.append('exportFormat', exportFormat);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportVendorRefundsCsv = async (vendorId: string): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.invoiceUrl}/invoices/refunds/csv/export`);
  url.searchParams.append('vendorId', vendorId);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportFromInvoiceService = async (path: string, method: string, filterParams?: Record<string, any>, exportFormat: 'vendor' | 'operator' | undefined = 'operator'): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.invoiceUrl}${path}`);
  appendFilterParams(url, filterParams);

  if (exportFormat) {
    url.searchParams.append('exportFormat', exportFormat);
  }

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    method,
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const getClientMappingsExportCsv = async (params: api.ClientMappingFilterParams): Promise<BlobPart> => await downloadFile(`${CONFIG.api.invoiceUrl}/external-clients/csv/export`, params);

export const downloadInvoiceDocument = async (invoiceId: api.UUID, documentId: api.UUID): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.invoiceUrl}/invoices/${invoiceId}/documents/${documentId}/download`);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  if (response.status !== 200) {
    const error = await response.json();
    throw new Error(error?.message || response.statusText);
  }

  return await response.blob();
};

export const exportAllAdjustmentsOriginatingBySubType = async (adjustmentSubType: api.AdjustmentCredit | api.AdjustmentDebit, createdBeginningAt?: api.ISODateString, createdEndingAt?: api.ISODateString): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.invoiceUrl}/invoice-adjustments/originating-by-subtype/csv/export`);

  url.searchParams.append('adjustmentSubType', adjustmentSubType);
  if (createdBeginningAt) {
    url.searchParams.append('createdBeginningAt', createdBeginningAt);
  }
  if (createdEndingAt) {
    url.searchParams.append('createdEndingAt', createdEndingAt);
  }

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportAllAdjustmentsBySubType = async (adjustmentSubType: api.AdjustmentCredit | api.AdjustmentDebit, createdBeginningAt?: api.ISODateString, createdEndingAt?: api.ISODateString): Promise<BlobPart> => {

  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.invoiceUrl}/invoice-adjustments/export/csv`);

  url.searchParams.append('adjustmentSubType', adjustmentSubType);
  if (createdBeginningAt) {
    url.searchParams.append('createdBeginningAt', createdBeginningAt);
  }
  if (createdEndingAt) {
    url.searchParams.append('createdEndingAt', createdEndingAt);
  }
  url.searchParams.append('page', '1');
  url.searchParams.append('limit', Number.MAX_SAFE_INTEGER.toString());

  const response = await fetch(url.href, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportSvbFailedTransferLineItems = async (): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.invoiceUrl}/invoice-line-items/svb-failed-transfers/csv/export`);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  if (response.status !== 200) {
    const error = await response.json();
    throw new Error(error?.message || response.statusText);
  }

  return await response.blob();
};

export const exportSvbLoanTapeReport = async (
  fundedBeginningAt: api.ISODateString,
  fundedEndingAt: api.ISODateString,
): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.invoiceUrl}/invoice-line-items/svb-loan-tape/csv/export`);
  url.searchParams.append('fundedBeginningAt', fundedBeginningAt);
  url.searchParams.append('fundedEndingAt', fundedEndingAt);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  if (response.status !== 200) {
    const error = await response.json();
    throw new Error(error?.message || response.statusText);
  }

  return await response.blob();
};

export const downloadFile = async (
  endpointUrl: string,
  requestParams: any,
): Promise<BlobPart> => {
  const idToken = await getIdToken();
  const url = new URL(endpointUrl);
  appendFilterParams(url, requestParams);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportVendorsAdjustmentBalancesCsv = async (params: VendorAdjustmentBalancesExportParams): Promise<BlobPart> => await downloadFile(`${CONFIG.api.invoiceUrl}/invoice-adjustments/all-vendors/balance/csv/export`, params);
export const getInvoiceRevenueByVendorExportCsv = async (params: api.InvoiceRevenueByVendorParams): Promise<BlobPart> => await downloadFile(`${CONFIG.api.invoiceUrl}/invoices/statistics/revenue/by-vendor/export/csv`, params);
export const getInvoiceRevenueByClientExportCsv = async (params: api.InvoiceRevenueByClientParams): Promise<BlobPart> => await downloadFile(`${CONFIG.api.invoiceUrl}/invoices/statistics/revenue/by-client/export/csv`, params);
export const getInvoiceRevenueByPartnerExportCsv = async (params: api.InvoiceRevenueByPartnerParams): Promise<BlobPart> => await downloadFile(`${CONFIG.api.invoiceUrl}/invoices/statistics/revenue/by-partner/export/csv`, params);

export const getInvoiceCohortsSeriesExportCsv = async (params: api.InvoiceCohortsSeriesParams): Promise<BlobPart> => await downloadFile(`${CONFIG.api.invoiceUrl}/invoices/statistics/cohorts/export/csv`, params);
export const getInvoiceCohortVendorsSeriesExportCsv = async (cohort: api.ISODateString, params: api.InvoiceCohortVendorsSeriesParams): Promise<BlobPart> => await downloadFile(`${CONFIG.api.invoiceUrl}/invoices/statistics/cohorts/${cohort}/vendors/export/csv`, params);
export const downloadImportedFile = async (id: api.UUID): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.invoiceUrl}/operations/imported-files/${id}/download`);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  if (response.status !== 200) {
    const error = await response.json().catch(() => undefined);
    throw new Error(error?.message || response.statusText);
  }

  return await response.blob();
};
